import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Publication } from '@shared/types/publication';
import PublicationCard from '@shared/components/common/PublicationCard';
import CategoryFilter from './CategoryFilter';
import TitleAccent from './TitleAccent';
import { Button } from '@shared/components/common/Button';
import useMasterData from '@shared/hooks/useMasterData';
import { useStore } from '@shared/hooks/useStore';

interface CategoryPublicationsSectionProps {
    onPublicationClick?: (publication: Publication) => void,
    onQuickView?: (publication: Publication) => void
}

const CategoryPublicationsSection = observer(({
    onPublicationClick,
    onQuickView
}: CategoryPublicationsSectionProps) => {
    const navigate = useNavigate();
    const { publicationStore, apiStore } = useStore();
    const { categoryPublications: publications, totalCategory: totalRecord } = publicationStore;
    const isLoading = apiStore.isLoading;

    const [selectedCategory, setSelectedCategory] = useState('all');
    const { categories } = useMasterData();

    const fetchPublications = async () => {
        const params = selectedCategory !== 'all' ? { categoryCodes: selectedCategory } : {};
        publicationStore.fetchCategoryPublications({
            ...params,
            size: 12
        });
    };

    useEffect(() => {
        fetchPublications();
    }, [selectedCategory]);

    const handleCategoryChange = (categoryId: string) => {
        setSelectedCategory(categoryId);
    };

    const handleShowMore = () => {
        const path = selectedCategory === 'all' ? '/publications' : `/publications?categoryCodes=${selectedCategory}`;
        navigate(path);
    };

    return (
        <section className='container mx-auto px-4 py-8 md:py-[60px]'>
            <div className='flex items-center justify-between mb-6 md:mb-[30px]'>
                <div className='flex items-center gap-[4px]'>
                    <TitleAccent />
                    <h2 className='font-semibold text-[20px] leading-[24px]'>
                        カテゴリ別商品一覧
                    </h2>
                </div>
                <div className='flex-1 ml-[10px] h-[1px] bg-border' />
            </div>

            <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
            />

            {isLoading ?
                (
                    <></>
                ) :
                (
                    <>
                        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-[24px] lg:gap-[36px] xl:gap-[48px]'>
                            {publications.map(publication => (
                                <PublicationCard
                                    key={publication.id}
                                    publication={publication}
                                    onClick={onPublicationClick}
                                    onQuickView={onQuickView}
                                />
                            ))}
                        </div>

                        {publications.length === 0 && (
                            <div className='text-center py-20 text-text-secondary'>
                                該当する商品が見つかりませんでした。
                            </div>
                        )}
                    </>
                )}

            {totalRecord > 12 && (
                <div className='flex justify-center mt-6 md:mt-10'>
                    <Button
                        onClick={handleShowMore}
                        className='bg-primary-light py-[12px] px-[26px] rounded-[10px] font-semibold leading-[16px] text-white'
                    >
                        もっと見る
                    </Button>
                </div>
            )}
        </section>
    );
});

export default CategoryPublicationsSection;
